import { useCallback } from 'react';
import { useLocale } from '@hooks/useLocale';
import { Address } from '@adapters/bigcommerce/types/address';
import useAddresses from './useAddresses';
import { HookError } from '../../errorHandlers';

export const saveAddressFetcher = async (
  address: Address,
  locale: string
): Promise<Address[]> => {
  if (!address) {
    throw new HookError('Address must be provided to update the address');
  }
  const saveAddressResponse = await fetch('/api/customer/save-address', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': locale,
    },
    body: JSON.stringify(address),
  });

  if (saveAddressResponse.status !== 200) {
    throw new HookError('Add address request failed');
  }

  const { data } = await saveAddressResponse.json();
  return data ?? [];
};

const useSaveAddress = () => {
  const { data = [], mutate } = useAddresses();
  const locale = useLocale();
  if (!locale) throw new HookError('Locale must be specified to login');

  return useCallback(
    async (address: Address) => {
      const savedAddresses = await saveAddressFetcher(address, locale);
      mutate([...data, ...savedAddresses]);
      return savedAddresses;
    },
    [data, mutate, locale]
  );
};

export default useSaveAddress;
